/**
 * TITAM AI - Componentes UX Premium
 * Microinterações, feedback visual e acessibilidade
 */

import { useState, useEffect, useRef } from 'react'
import Icon from './icons.jsx'

// ── Skeleton Loading ───────────────────────────────────────────────────────────
export function Skeleton({ width, height, circle, style }) {
  return (
    <div
      className="skeleton"
      style={{
        width: width || '100%',
        height: height || 20,
        borderRadius: circle ? '50%' : 8,
        ...style
      }}
    />
  )
}

// ── Loading Spinner ──────────────────────────────────────────────────────────
function Spinner({ size = 24, color = 'var(--text)' }) {
  return (
    <div className="spinner" style={{ width: size, height: size }}>
      <svg viewBox="0 0 24 24" style={{ width: '100%', height: '100%' }}>
        <circle
          cx="12"
          cy="12"
          r="10"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          style={{
            strokeDasharray: '60, 100',
            transformOrigin: 'center',
            animation: 'spin 1s linear infinite'
          }}
        />
      </svg>
    </div>
  )
}

// ── Tooltip ─────────────────────────────────────────────────────────────────
function Tooltip({ children, text, position = 'top' }) {
  const [show, setShow] = useState(false)
  const positions = {
    top: { bottom: '100%', left: '50%', transform: 'translateX(-50%) translateY(-8px)' },
    bottom: { top: '100%', left: '50%', transform: 'translateX(-50%) translateY(8px)' },
    left: { right: '100%', top: '50%', transform: 'translateY(-50%) translateX(-8px)' },
    right: { left: '100%', top: '50%', transform: 'translateY(-50%) translateX(8px)' }
  }

  return (
    <div
      className="tooltip-wrapper"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div className="tooltip" style={positions[position]}>
          {text}
        </div>
      )}
    </div>
  )
}

// ── Toast Melhorado ─────────────────────────────────────────────────────────
function Toast({ message, type = 'info', onClose, duration = 4000 }) {
  const [progress, setProgress] = useState(100)
  const icons = {
    success: 'check',
    error: 'x',
    warning: 'suggest',
    info: 'ai'
  }
  const colors = {
    success: 'var(--green)',
    error: 'var(--red)',
    warning: 'var(--amber)',
    info: 'var(--blue)'
  }

  useEffect(() => {
    const start = Date.now()
    const interval = setInterval(() => {
      const elapsed = Date.now() - start
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100)
      setProgress(remaining)
      if (remaining === 0) {
        clearInterval(interval)
        onClose()
      }
    }, 16)
    return () => clearInterval(interval)
  }, [duration, onClose])

  return (
    <div className={`toast-card toast-${type}`}>
      <div className="toast-icon" style={{ color: colors[type] }}>
        <Icon n={icons[type]} s={18} />
      </div>
      <div className="toast-content">
        <div className="toast-message">{message}</div>
      </div>
      <button className="toast-close" onClick={onClose} aria-label="Fechar notificação">
        <Icon n="x" s={14} />
      </button>
      <div className="toast-progress" style={{ width: `${progress}%`, background: colors[type] }} />
    </div>
  )
}

// ── Confirmação Modal ────────────────────────────────────────────────────────
export function ConfirmModal({ isOpen, title, message, onConfirm, onCancel, confirmText = 'Confirmar', cancelText = 'Cancelar', danger }) {
  if (!isOpen) return null

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
        </div>
        <div className="modal-body">
          <p className="modal-message">{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onCancel}>{cancelText}</button>
          <button className={`btn ${danger ? 'btn-red' : 'btn-black'}`} onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Breadcrumb Melhorado ─────────────────────────────────────────────────────
function Breadcrumb({ items }) {
  const navigate = useNavigate()
  return (
    <nav aria-label="Breadcrumb" className="breadcrumb">
      {items.map((item, index) => (
        <span key={index} className="breadcrumb-item">
          {index > 0 && <span className="breadcrumb-separator">/</span>}
          {item.active ? (
            <span className="breadcrumb-current" aria-current="page">{item.label}</span>
          ) : (
            <button className="breadcrumb-link" onClick={() => navigate(item.path)}>
              {item.label}
            </button>
          )}
        </span>
      ))}
    </nav>
  )
}

// ── Toggle Switch ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, label, disabled }) {
  return (
    <label className={`toggle ${disabled ? 'disabled' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="toggle-slider" />
      {label && <span className="toggle-label">{label}</span>}
    </label>
  )
}

// ── Search Input com Autocomplete ──────────────────────────────────────────
export function SearchInput({ placeholder, value, onChange, suggestions, onSelect }) {
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [focused, setFocused] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filtered = suggestions?.filter(s =>
    s.toLowerCase().includes(value.toLowerCase())
  ) || []

  return (
    <div ref={wrapperRef} className="search-input-wrapper">
      <div className={`search-input ${focused ? 'focused' : ''}`}>
        <Icon n="search" s={16} c="var(--ter)" />
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={e => { onChange(e.target.value); setShowSuggestions(true) }}
          onFocus={() => { setFocused(true); setShowSuggestions(true) }}
          onBlur={() => setFocused(false)}
        />
        {value && (
          <button className="search-clear" onClick={() => onChange('')} aria-label="Limpar busca">
            <Icon n="x" s={14} />
          </button>
        )}
      </div>
      {showSuggestions && filtered.length > 0 && (
        <div className="search-suggestions">
          {filtered.slice(0, 5).map((s, i) => (
            <button key={i} className="search-suggestion" onClick={() => { onSelect(s); setShowSuggestions(false) }}>
              <Icon n="search" s={14} c="var(--ter)" />
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Avatar com Iniciais ─────────────────────────────────────────────────────
function Avatar({ name, size = 32, src, online }) {
  const initials = name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'

  return (
    <div className="avatar-wrapper" style={{ width: size, height: size }}>
      {src ? (
        <img src={src} alt={name} className="avatar-img" />
      ) : (
        <div className="avatar-fallback" style={{ width: size, height: size, fontSize: size * 0.4 }}>
          {initials}
        </div>
      )}
      {online && <span className="avatar-online-indicator" />}
    </div>
  )
}

// Hook para useNavigate (para evitar erro se não estiver em Router)
import { useNavigate as useRouterNavigate } from 'react-router-dom'

function useNavigate() {
  try {
    return useRouterNavigate()
  } catch {
    return () => {}
  }
}
